import { useEffect } from 'react';
import { sqSummaryReportStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import {
  type SetStoreState,
  type StoreInstance,
  createStore,
  createStoreInstance,
  useRegisterPersistedStore,
} from '@/stores';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { INPUT_NAMES } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';

import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolItem,
} from '@/toolSelection/baseTool.store';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';

/** InputFeatureV1 extended with runtime fields not yet reflected in the generated SDK type */
interface InputFeatureRuntime extends InputFeatureV1 {
  inputText?: string | null;
  inputItem?: string | null;
  inputNumeric?: number | null;
  inputNumericUom?: string | null;
}

interface OperationalizeStatisticsSummaryState extends BaseToolPersistedState {
  id?: string;
  name: string;
  color: string;
  baseline: StatisticsSummaryItem | null;
  inputSignal: ToolItem | null;
  condition: ToolItem | null;
  controlCharts: string[];
  originalParameters: ToolItem[];
  configParams: ToolConfigParams;
}

export interface OperationalizeStatisticsSummaryActions {
  setId: (id: string) => void;
  setName: (name: string) => void;
  setColor: (color: string) => void;
  setInputSignal: (signal: ToolItem | null) => void;
  setCondition: (condition: ToolItem | null) => void;
  setBaseline: (baseline: StatisticsSummaryItem | null) => void;
  setControlCharts: (controlCharts: string[]) => void;
  setOriginalParameters: (parameters: ToolItem[]) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setConfigParams: (configParams: ToolConfigParams) => void;
  rehydrate: (payload: ModelRehydratePayload) => void;
  reset: () => void;
}

export type LegacyOperationalizeStatisticsSummaryStore = StoreInstance<
  OperationalizeStatisticsSummaryState,
  OperationalizeStatisticsSummaryActions
> & {
  readonly persistenceLevel: 'WORKSHEET';
  dehydrate: () => OperationalizeStatisticsSummaryDehydratedState;
  rehydrate: (dehydratedState: Partial<OperationalizeStatisticsSummaryDehydratedState>) => void;
  advancedParametersCollapsed: boolean;
};

const buildInitialState = (): OperationalizeStatisticsSummaryState => ({
  id: undefined,
  color: '',
  name: '',
  baseline: null,
  controlCharts: [],
  inputSignal: null,
  condition: null,
  originalParameters: [],
  advancedParametersCollapsed: true,
  configParams: { type: '', advancedParametersCollapsed: true },
});

export type OperationalizeStatisticsSummaryDehydratedState = Pick<
  OperationalizeStatisticsSummaryState,
  'id' | 'name' | 'controlCharts' | 'condition' | 'inputSignal' | 'baseline' | 'advancedParametersCollapsed'
>;

const operationalizeStatisticsSummaryActionsFactory = (
  set: SetStoreState<OperationalizeStatisticsSummaryState>,
): OperationalizeStatisticsSummaryActions => ({
  setId: (id: string) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.id = id;
    }),

  setName: (name: string) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.name = name;
    }),

  setColor: (color: string) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.color = color;
    }),

  setInputSignal: (signal: ToolItem | null) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.inputSignal = signal;
    }),

  setCondition: (condition: ToolItem | null) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.condition = condition;
    }),

  setBaseline: (baseline: StatisticsSummaryItem | null) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.baseline = baseline;
    }),

  setControlCharts: (controlCharts: string[]) =>
    set((state: OperationalizeStatisticsSummaryState) => {
      state.controlCharts = controlCharts;
    }),

  setOriginalParameters: (parameters: ToolItem[]) =>
    set((draft) => {
      draft.originalParameters = parameters;
    }),

  setAdvancedParametersCollapsed: (collapsed: boolean) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setConfigParams: (configParams: ToolConfigParams) =>
    set((draft) => {
      draft.configParams = configParams;
    }),

  rehydrate: (payload: ModelRehydratePayload): void =>
    set((state: OperationalizeStatisticsSummaryState) => {
      if (payload.type !== TREND_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY) {
        return;
      }
      state.id = payload.instanceId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: InputFeatureRuntime): void => {
        const inputName: string = feature.name ?? '';
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            state.name = payload.instanceId ? String(feature.inputText ?? '') : `${feature.inputText ?? ''} (Copy)`;
            break;
          case INPUT_NAMES.signalInput: {
            const itemId = feature.inputItem ?? '';
            const item = sqTrendSeriesStore.findItem(itemId) || { id: itemId };
            state.inputSignal = { id: item.id, name: (item as ToolItem).name } as ToolItem;
            break;
          }
          case INPUT_NAMES.restrictOutputToConditionInput: {
            const itemId = feature.inputItem;
            if (itemId) {
              const item = sqTrendConditionStore.findItem(itemId) || { id: itemId };
              state.condition = { id: item.id, name: (item as ToolItem).name } as ToolItem;
            }
            break;
          }
          case INPUT_NAMES.baselineInput: {
            const itemId = feature.inputItem;
            if (itemId) {
              const item = sqSummaryReportStore.findItem(itemId) || { id: itemId };
              state.controlCharts = Object.keys(item?.data?.spcCharts || {});
              state.baseline = item as StatisticsSummaryItem;
            }
            break;
          }
          default:
            break;
        }
      });

      if (payload.modelData) {
        state.configParams = payload.modelData as unknown as ToolConfigParams;
      }
    }),

  reset: () => set(() => buildInitialState()),
});

export const dehydrateOperationalizeStatisticsSummaryState = (
  state: OperationalizeStatisticsSummaryState,
): OperationalizeStatisticsSummaryDehydratedState => ({
  id: state.id,
  name: state.name,
  inputSignal: state.inputSignal,
  controlCharts: state.controlCharts,
  condition: state.condition,
  baseline: state.baseline,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
});

const [
  OperationalizeStatsSummaryProvider,
  useOperationalizeStatisticsSummaryStoreInstance,
  useOperationalizeStatisticsSummarySelector,
] = createStore(
  'sqOperationalizeStatisticsSummaryStore',
  buildInitialState(),
  operationalizeStatisticsSummaryActionsFactory,
);

export function legacyCreateOperationalizeStatisticsSummaryStore(): LegacyOperationalizeStatisticsSummaryStore {
  const store = createStoreInstance(buildInitialState(), operationalizeStatisticsSummaryActionsFactory);

  return Object.assign(store, {
    persistenceLevel: 'WORKSHEET' as const,
    dehydrate: () => dehydrateOperationalizeStatisticsSummaryState(store.getState()),
    rehydrate: (dehydratedState: Partial<OperationalizeStatisticsSummaryDehydratedState>) => {
      store.setState(dehydratedState, false);
    },
    advancedParametersCollapsed: store.getState().advancedParametersCollapsed,
  });
}

export { OperationalizeStatsSummaryProvider };

/** Fine-grained selectors — each causes a re-render only when its own field changes. */
export const useOperationalizeStatsSummaryId = () => useOperationalizeStatisticsSummarySelector((state) => state.id);
export const useOperationalizeStatsSummaryName = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.name);
export const useOperationalizeStatsSummaryColor = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.color);
export const useOperationalizeStatsSummaryBaseline = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.baseline);
export const useOperationalizeStatsSummaryInputSignal = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.inputSignal);
export const useOperationalizeStatsSummaryCondition = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.condition);
export const useOperationalizeStatsSummaryControlCharts = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.controlCharts);
export const useOperationalizeStatsSummaryOriginalParameters = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.originalParameters);
export const useOperationalizeStatsSummaryAdvancedParametersCollapsed = () =>
  useOperationalizeStatisticsSummarySelector((state) => state.advancedParametersCollapsed);

export const useOperationalizeStatsSummaryState = () => useOperationalizeStatisticsSummarySelector((state) => state);

/** Actions for managing state. */
export const useOperationalizeStatsSummaryActions = () => useOperationalizeStatisticsSummaryStoreInstance().actions;

export const usePersistOperationalizeStatisticsSummaryStore = () => {
  const register = useRegisterPersistedStore();
  const store = useOperationalizeStatisticsSummaryStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqOperationalizeStatisticsSummaryStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateOperationalizeStatisticsSummaryState,
      reset: (initialState) => {
        store.setState(initialState, true);
      },
    });
  }, [register, store]);
};
